import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Helmet from 'react-helmet';
import {
    Page,
    PageContent,
    Heading,
    Spacer,
    TextButton,
} from '@zillow/constellation';
import ZRMUtils from '@zg-rentals/zrm-utils-js/lib/utils';

// Misc / utils
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import { RoutingSelectors, UserSelectors } from 'app/shared/selectors';
import ZillowHomeLogo from 'app/shared/images/zillow-home-logo.svg';

import loadable from '@loadable/component';

const Widget = loadable(() => import('@zg-rentals/rasa-zrm-chatbot-js'));

const SOCKET_URLS = {
    DEV: 'https://dialog.conversational-ai-dev.zg-int.net/',
    PROD: 'https://tid.zillow.com/',
    TEST: 'http://convai-zrm-bot.dev.ana.zillow.net:8501',
};

const CHAT_SESSION = 'chat_session';

const ChatBotTestPage = () => {
    const dispatch = useDispatch();
    const { userToken } = useSelector(state => UserSelectors.makeUser(state));
    const queryString = useSelector(state => RoutingSelectors.makeSearch(state));
    const pathname = useSelector(state => RoutingSelectors.makePathname(state));
    const queryParams = ZRMUtils.queryUtils.parse(queryString);
    const isDevUrl = queryParams && queryParams.socketUrl === 'dev';
    const isTestUrl = queryParams && queryParams.socketUrl === 'test';
    let socketUrl = SOCKET_URLS.PROD;
    if (isDevUrl) {
        socketUrl = SOCKET_URLS.DEV;
    }
    if (isTestUrl) {
        socketUrl = SOCKET_URLS.TEST;
    }

    const getDomain = () => {
        try {
            return window.location.origin;
        } catch (err) {
            return 'https://www.zillow.com/';
        }
    };

    const handleOpen = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CHATBOT_OPEN
        ));
    };

    const handleClose = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CHATBOT_CLOSE
        ));
    };

    const handleToggleFullscreen = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CHATBOT_FULLSCREEN
        ));
    };

    const handleClearChatSession = () => {
        try {
            window.localStorage.removeItem(CHAT_SESSION);
        } catch (err) {
            /* eslint-disable no-console */
            console.log('error clearing localStorage: ', err);
        }
    };

    useEffect(() => () => {
        handleClose();
    },
    []);

    const customMessageDelay = (message) => {
        // custom delay in ms
        const MAX_DELAY = 1500;
        const MIN_DELAY = 600;
        let delay = message.length * 30;
        if (delay > MAX_DELAY) delay = MAX_DELAY;
        if (delay < MIN_DELAY) delay = MIN_DELAY;
        return delay;
    };

    return (
        <Page>
            <Helmet
                meta={[
                    {
                        name: 'robots',
                        content: 'noindex, nofollow',
                    },
                ]}
            />

            <PageContent paddingX="xl">
                <Heading level={3}>Chatbot Test Page</Heading>
                <Spacer marginY={4} />
                    Current socket url:
                {' '}
                <strong>{socketUrl}</strong>
                <br />
                <br />
                    Change socket url to
                {' '}
                <a href={isDevUrl ? getDomain() + pathname : `${getDomain() + pathname}?socketUrl=dev`}>
                    <strong>{isDevUrl ? SOCKET_URLS.PROD : SOCKET_URLS.DEV}</strong>
                </a>
                <br />
                <br />
                <TextButton onClick={handleClearChatSession}>Clear chat session</TextButton>
                {' '}
                    (reload page to see changes)
                <Widget
                    initPayload="/get_started"
                    socketUrl={socketUrl}
                    socketPath="/rental-manager/chat"
                    profileAvatar={ZillowHomeLogo}
                    customData={{ language: 'en' }}
                    customMessageDelay={customMessageDelay}
                    title="Zillow Rentals Assistant"
                    sessionToken={userToken}
                    inputTextFieldHint="Ask a question"
                    showCloseButton={true}
                    showFullScreenButton={true}
                    onWidgetEvent={{
                        onChatOpen: handleOpen,
                        onChatClose: handleClose,
                        onChatFullScreen: handleToggleFullscreen,
                    }}
                />
                <Spacer marginY={40} />
            </PageContent>
        </Page>
    );
};

export default ChatBotTestPage;
